import type { AgendaItem } from "@elation/api-client/src/stitch-web";

import { AgendaItemEntity } from "@src/features/agenda/domain";

export type ReconcileOrderedAgendaItemsOptions = {
  /**
   * When true, keep the current persisted-item ID sequence and only refresh
   * payloads from incoming. Used while a local reorder owns visible order.
   */
  preservePersistedOrder?: boolean;
};

/**
 * Whether an agenda item id is a local optimistic create placeholder.
 */
export function isPendingAgendaItemId(id: number): boolean {
  return id < 0;
}

function reconcilePreservingPersistedOrder(
  current: readonly AgendaItem[],
  actionableIncoming: readonly AgendaItem[],
  pendingCreateIds: ReadonlySet<number>,
  completedCreateIds: ReadonlyMap<number, number>,
): AgendaItem[] {
  const incomingById = new Map(
    actionableIncoming.map((item) => [item.id, item]),
  );
  const usedIncomingIds = new Set<number>();
  const currentPersistedIds = new Set(
    current
      .filter((item) => !isPendingAgendaItemId(item.id))
      .map((item) => item.id),
  );
  const completedCreateServerIds = new Set(completedCreateIds.values());
  const nextItems: AgendaItem[] = [];

  for (const item of current) {
    if (isPendingAgendaItemId(item.id)) {
      const completedCreateId = completedCreateIds.get(item.id);
      if (completedCreateId != null) {
        const incomingItem = incomingById.get(completedCreateId);
        if (incomingItem) {
          usedIncomingIds.add(incomingItem.id);
          nextItems.push(incomingItem);
          continue;
        }
      }

      if (pendingCreateIds.has(item.id)) {
        const matchingCreatedItem = actionableIncoming.find(
          (incomingItem) =>
            !usedIncomingIds.has(incomingItem.id) &&
            !currentPersistedIds.has(incomingItem.id) &&
            incomingItem.description === item.description &&
            incomingItem.type === item.type,
        );

        if (matchingCreatedItem) {
          usedIncomingIds.add(matchingCreatedItem.id);
          nextItems.push(matchingCreatedItem);
          continue;
        }

        nextItems.push(item);
      }
      continue;
    }

    const incomingItem = incomingById.get(item.id);
    if (incomingItem) {
      usedIncomingIds.add(incomingItem.id);
      nextItems.push(incomingItem);
      continue;
    }

    if (completedCreateServerIds.has(item.id)) {
      nextItems.push(item);
    }
  }

  for (const item of actionableIncoming) {
    if (!usedIncomingIds.has(item.id)) {
      nextItems.push(item);
    }
  }

  return nextItems;
}

/**
 * Reconciles local ordered (actionable) UI state with an incoming server list.
 *
 * By default, incoming server/SSE order is authoritative for persisted items.
 * With `preservePersistedOrder`, the current persisted ID sequence is kept while
 * payloads refresh from incoming. Unresolved create placeholders remain.
 */
export function reconcileOrderedAgendaItems(
  current: readonly AgendaItem[],
  incoming: readonly AgendaItem[],
  pendingCreateIds: ReadonlySet<number>,
  completedCreateIds: ReadonlyMap<number, number>,
  options: ReconcileOrderedAgendaItemsOptions = {},
): AgendaItem[] {
  const actionableIncoming = incoming.filter(AgendaItemEntity.isActionable);

  if (options.preservePersistedOrder) {
    return reconcilePreservingPersistedOrder(
      current,
      actionableIncoming,
      pendingCreateIds,
      completedCreateIds,
    );
  }

  const incomingById = new Map(
    actionableIncoming.map((item) => [item.id, item]),
  );
  const usedIncomingIds = new Set<number>();
  const currentPersistedIds = new Set(
    current
      .filter((item) => !isPendingAgendaItemId(item.id))
      .map((item) => item.id),
  );
  const completedCreateServerIds = new Set(completedCreateIds.values());

  type PendingSlot = { afterId: number | null; item: AgendaItem };
  const pendingSlots: PendingSlot[] = [];
  const retainedCompletedCreates: AgendaItem[] = [];
  let lastPlacedPersistedId: number | null = null;

  for (const item of current) {
    if (isPendingAgendaItemId(item.id)) {
      const completedCreateId = completedCreateIds.get(item.id);
      if (completedCreateId != null) {
        const incomingItem = incomingById.get(completedCreateId);
        if (incomingItem) {
          usedIncomingIds.add(incomingItem.id);
          lastPlacedPersistedId = incomingItem.id;
          continue;
        }
      }

      if (pendingCreateIds.has(item.id)) {
        const matchingCreatedItem = actionableIncoming.find(
          (incomingItem) =>
            !usedIncomingIds.has(incomingItem.id) &&
            !currentPersistedIds.has(incomingItem.id) &&
            incomingItem.description === item.description &&
            incomingItem.type === item.type,
        );

        if (matchingCreatedItem) {
          usedIncomingIds.add(matchingCreatedItem.id);
          lastPlacedPersistedId = matchingCreatedItem.id;
          continue;
        }

        pendingSlots.push({ afterId: lastPlacedPersistedId, item });
      }
      continue;
    }

    if (incomingById.has(item.id)) {
      lastPlacedPersistedId = item.id;
      continue;
    }

    if (completedCreateServerIds.has(item.id)) {
      retainedCompletedCreates.push(item);
      lastPlacedPersistedId = item.id;
    }
  }

  const nextItems = actionableIncoming.map((item) => {
    usedIncomingIds.add(item.id);
    return item;
  });

  for (const retained of retainedCompletedCreates) {
    if (!nextItems.some((item) => item.id === retained.id)) {
      nextItems.push(retained);
    }
  }

  // Iterate in reverse. Repeatedly inserting at the same anchor (splice at
  // neighborIndex + 1, or unshift for a null anchor) always lands the newest
  // insertion immediately after the anchor, ahead of anything already
  // inserted there. Walking pendingSlots forward would therefore invert the
  // relative order of slots that share an anchor; walking it backward
  // inserts the local-last slot first, so each subsequent (earlier) slot
  // gets pushed back in front of it, restoring the original order.
  for (let i = pendingSlots.length - 1; i >= 0; i--) {
    const slot = pendingSlots[i];
    if (slot.afterId == null) {
      nextItems.unshift(slot.item);
      continue;
    }

    const neighborIndex = nextItems.findIndex(
      (item) => item.id === slot.afterId,
    );
    if (neighborIndex === -1) {
      nextItems.push(slot.item);
      continue;
    }

    nextItems.splice(neighborIndex + 1, 0, slot.item);
  }

  return nextItems;
}
